<template>
   <div class="relative mx-auto w-full max-w-lg">
      <div class="w-full bg-black" style="padding-top: 56.25%">
         <transition-group name="sano-fade-fix-vertical">
            <div v-show="showPlayer" key="player_shown" class="absolute left-0 top-0 size-full">
               <div :id="`${videoId}-player`"></div>
            </div>
            <button
               v-show="!showPlayer"
               key="player_hidden"
               :ref="playButton"
               class="border-grey-light absolute left-0 top-0 size-full cursor-pointer"
               aria-label="play"
               @click="showAndPlay"
            >
               <LazyLoadingBGimage v-if="posterImageUrl" :mainSrc="posterImageUrl" />
            </button>
         </transition-group>
      </div>
   </div>
</template>

<script setup lang="ts">
import { type VNodeRef } from "vue";
import LazyLoadingBGimage from "./LazyLoadingBGimage.vue";

interface YTWindow extends Window {
   YT: any;
   onYouTubeIframeAPIReady: () => void;
   Player: any;
}

const props = withDefaults(
   defineProps<{
      videoId: string;
      posterImageUrl?: string;
   }>(),
   {
      videoId: "",
      posterImageUrl: "",
   }
);

const showPlayer = ref(false);
const playFirstClicked = ref(false);
const ytPlayer: any = ref(null);
const playButton = ref<VNodeRef | null>(null);
const ytWindow = window as any as YTWindow;

watch(playFirstClicked, (val: boolean) => {
   if (val) setupVideo();
});

const setupVideo = () => {
   if (!ytWindow.YT) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      const firstScriptTag = document.getElementsByTagName("script")[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      ytWindow.onYouTubeIframeAPIReady = () => {
         setTimeout(() => {
            onYTApiReady();
         }, 0);
      };
   } else {
      onYTApiReady();
   }
};

const onYTApiReady = () => {
   try {
      ytPlayer.value = new ytWindow.YT.Player(`${props.videoId}-player`, {
         height: "100%",
         width: "100%",
         videoId: props.videoId,
         playerVars: {
            playsinline: 0,
            modestbranding: 1,
         },
         events: {
            onReady: onPlayerReady,
            onStateChange: onPlayerStateChange,
            onError: onPlayerError,
         },
      });
   } catch (error) {
      console.error("Failed to create YouTube player:", error);
   }
};

const onPlayerReady = () => {
   // Player is now ready, we can safely call playVideo
   setTimeout(() => {
      if (ytPlayer.value && typeof ytPlayer.value.playVideo === "function") {
         ytPlayer.value.playVideo();
      }
      showPlayer.value = true;
   }, 1000);
};

const onPlayerError = (event: any) => {
   console.error("YouTube player error:", event);
   // Optionally, you could show an error message to the user or fallback to a different display
};

const onPlayerStateChange = (e: any) => {
   // Use this function to pick up on events coming from the controls within the video player
   // Video is paused [2] or video is ended [0]
   if (e.data === 0) {
      showPlayer.value = false;
      nextTick(() => {
         if (playButton.value) playButton.value.focus();
      });
   }
   // Video is playing (if the user skips ahead, the video may pause, buffer, then play, so we need to re-show the player)
   if (e.data === 1) {
      nextTick(() => {
         const youtubePlayer = document.getElementById(`${props.videoId}-player`);
         if (youtubePlayer) youtubePlayer.focus();
      });
   }
};

const showAndPlay = () => {
   playFirstClicked.value = true;
   showPlayer.value = true;
   // This might either be clicked after the user has paused the video, or for the first time, before setup
   if (ytPlayer.value && typeof ytPlayer.value.playVideo === "function") {
      ytPlayer.value.playVideo();
   }
};
</script>

<style scoped lang="scss">
.sano-fade-fix-vertical-enter-active,
.sano-fade-enter-active {
   transition: opacity 0.45s ease-in-out 0.45s;
}

.sano-fade-fix-vertical-leave-active,
.sano-fade-leave-active {
   transition: opacity 0.45s ease-in-out;
}

.sano-fade-fix-vertical-leave-active,
.sano-fade-fix-vertical-enter-active {
   position: absolute !important;
   top: 50%;
   transform: translateY(-50%);
}

.sano-fade-fix-vertical-leave-to,
.sano-fade-fix-vertical-enter,
.sano-fade-leave-to,
.sano-fade-enter {
   opacity: 0;
}
</style>
